import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function KlinikRengoeringPage() {
  return (
    <>
      <SEO
        title="Klinik rengøring"
        description="Erfaren og grundig rengøring af klinikker og behandlingsmiljøer i Havdrup og omegn. Rene, hygiejniske og indbydende rammer for patienter og personale."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d3836]/10 to-white py-12 sm:py-16 lg:py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-slate-800 transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span>Ydelser</span>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="font-semibold text-slate-900">Klinik rengøring</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0d3836] px-3.5 py-1 text-xs font-semibold text-white">
                <i className="fa-regular fa-star text-[11px]" aria-hidden="true" />
                Vores spidskompetence – Stor erfaring med klinikker
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Rengøring af klinikker og behandlingsmiljøer
              </h1>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
                Havdrup Rengøring har særlig erfaring med rengøring af klinikker og behandlingsmiljøer. En klinik skal fremstå ren og indbydende for både medarbejdere og besøgende. Derfor arbejder vi grundigt og tilpasser rengøringen til klinikkens lokaler, anvendelse og konkrete ønsker.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#154e4a] transition-colors text-center"
                >
                  <span>Få et tilbud til din klinik</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
                >
                  <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
                  <span className="no-break">31 34 16 32</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-md border border-slate-200/80 aspect-4/3">
                <img
                  src={imageUrl('/_assets/tandlaegeklinik-tandlaegestol-rent.jpg', 'width=800,quality=85,format=auto')}
                  alt="Professionel rengøring af tandlægeklinik med ren tandlægestol og behandlerrum"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grundig gennemgang af klinikrengøring */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Særlig grundighed og sans for detaljen
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                  <p>
                    Rengøring af klinikker adskiller sig fra almindelig rengøring. Det stiller skærpede krav til omhyggelighed, punktlighed og forståelse for, at både patienter, klienter og sundhedspersonale skal færdes i rene og trygge omgivelser hver eneste dag.
                  </p>
                  <p>
                    Hos Havdrup Rengøring har vi arbejdet med klinikker i mange sammenhænge og opbygget en dybdegående rutine for, hvordan behandlingsrum, venteværelser og sanitære faciliteter vedligeholdes optimalt.
                  </p>
                  <p>
                    Vi kan sammensætte en løsning ud fra, hvilke områder der skal rengøres, hvor ofte der er behov for rengøring, og hvilke praktiske forhold der skal tages højde for.
                  </p>
                </div>
              </div>

              {/* Hvad omfatter en aftale om klinikrengøring */}
              <div className="rounded-2xl border border-[#0d3836]/20 bg-[#0d3836]/5 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-4 flex items-center gap-2">
                  <i className="fa-regular fa-square-plus text-[#0d3836]" aria-hidden="true" />
                  Hvad kan indgå i rengøringen af klinikken?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Grundig aftørring og desinficerende aftørring af kontaktflader</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Behandlerrum og undersøgelsesrum med fokus på rene flader</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Venteværelser og reception så det fremstår pænt og indbydende</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Patienttoiletter og personalefaciliteter med kalkfri finish</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Vask og desinfektion af gulve tilpasset lokalets brug</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Bortskaffelse af affald og opfyldning af forbrugsartikler</span>
                  </div>
                </div>
              </div>

              {/* Hvad koster rengøring af klinik? */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-2xs">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                  Hvad koster rengøring af klinikker?
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Prisen afhænger af klinikkens areal, antal behandlerrum, opgavens omfang samt frekvensen (f.eks. daglig rengøring eller flere gange ugentligt). Vi udarbejder altid et individuelt tilbud tilpasset klinikkens drift.
                </p>
                <div className="mt-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3836] hover:text-[#154e4a] transition-colors"
                  >
                    <span>Få et tilbud tilpasset jeres klinik</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Hurtig kontakt sidebar */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display">
                  Tag en snak med os
                </h3>
                <p className="text-sm text-slate-600">
                  Kontakt os, hvis du ønsker en snak om rengøring af din klinik og et tilbud ud fra jeres konkrete behov.
                </p>

                <div className="space-y-4 pt-2">
                  <a
                    href="tel:31341632"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ring direkte</p>
                      <p className="text-base font-bold text-slate-900 no-break">31 34 16 32</p>
                    </div>
                  </a>

                  <a
                    href="mailto:havdruprengoring@gmail.com"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Send en e-mail</p>
                      <p className="text-sm font-semibold text-slate-900 no-break">havdruprengoring@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="rounded-xl bg-[#0d3836]/5 p-4 border border-[#0d3836]/15 text-xs text-slate-700 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-star text-[#0d3836]" aria-hidden="true" />
                    <span className="font-semibold text-slate-900">Mest erfaring inden for klinikker</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-clock text-[#0d3836]" aria-hidden="true" />
                    <span>Arbejder uden for klinikkens åbningstider</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
